import { Injectable, inject } from '@angular/core';
import { combineLatest, map, Observable } from 'rxjs';
import { filter, shareReplay, switchMap } from 'rxjs/operators';
import { AggregationKey, Aggregations, AnbieterAggregations } from '../model/aggregations';
import {
  FacettenAggregationsClient,
  FilterAggregationsBasisFilterParameter
} from './integration/facetten-aggregations.client';
import { BildungsbereichService } from './state/bildungsbereich.service';
import { BerufeService } from './suchseite/berufe.service';
import { CorrectionService } from './suchseite/correction.service';
import { CombinedFacettenUrlService } from './facetten/combined-facetten-url.services';
import { SuchwortService } from './suchseite/suchwort.service';
import { OrtService } from './suchseite/ort.service';
import { BildungszielService } from './suchseite/bildungsziel.service';

/**
 * Bündelt alle Parameter reaktiv und ruft die zugehörigen Seiten vom Backend ab und stellt
 * diese als Observable bereit.
 */
@Injectable({ providedIn: 'root' })
export class FilterAggregationsService {
  private readonly ortService = inject(OrtService);
  private readonly combinedFacettenUrlService = inject(CombinedFacettenUrlService);
  private readonly suchwortService = inject(SuchwortService);
  private readonly facettenAggregationsClient = inject(FacettenAggregationsClient);
  private readonly correctionService = inject(CorrectionService);
  private readonly berufeService = inject(BerufeService);
  private readonly bildungsbereichService = inject(BildungsbereichService);
  private readonly bildungszielService = inject(BildungszielService);

  /**
   * bereitet den Backend-Call zur Ermittlung der Filter-Aggregations vor,
   * in dem die Facetten- und Filter-Parameter aus den aktuellen Werten
   * der verschiedenen Services zusammengebaut werden
   */
  private readonly preparedRequest$: Observable<{
    facettenParameter: { param: string; ids: string[] }[];
    basisFilterParameter: FilterAggregationsBasisFilterParameter;
  }> = combineLatest([
    this.suchwortService.showRequiredInputState$,
    this.suchwortService.suchwort$,
    this.ortService.umkreisParameter$,
    this.combinedFacettenUrlService.combinedFacetteParameters$,
    this.bildungszielService.dkzIds$,
    this.ortService.ortsunabhaengig$,
    this.correctionService.correctionState$,
    this.berufeService.ids$,
    this.bildungsbereichService.bildungsbereich$
  ]).pipe(
    filter(([showRequiredInputState]) => {
      return !showRequiredInputState;
    }),
    map(
      ([
        _,
        suchwort,
        umkreis,
        facettenParameter,
        dkzIds,
        ortsunabhaengig,
        correctionState,
        berufe,
        bildungsbereich
      ]) => ({
        facettenParameter,
        basisFilterParameter: {
          dkzIds,
          suchwort,
          umkreis,
          ortsunabhaengig,
          correctionState,
          berufe,
          bildungsbereich
        }
      })
    )
  );

  public filterAggregations$: Observable<Aggregations> = this.preparedRequest$.pipe(
    switchMap(({ facettenParameter, basisFilterParameter }) => {
      return this.facettenAggregationsClient.getFilterAggregations(
        facettenParameter,
        basisFilterParameter
      );
    }),
    shareReplay()
  );

  public count$ = this.filterAggregations$.pipe(
    map((aggregations) => aggregations[AggregationKey.AnzahlGesamt].COUNT)
  );

  /**
   * führt einen separaten Backend-Call zur Ermittlung der Anbieter-Filter-Aggregations aus,
   * weil die Anbieter-Liste zu groß für die gemeinsame Abfrage aller Filter-Aggregations ist
   * und eine Pagination enthält
   */
  public anbieterFilterAggregations$: Observable<AnbieterAggregations> = this.preparedRequest$.pipe(
    switchMap(({ facettenParameter, basisFilterParameter }) => {
      return this.facettenAggregationsClient.getAnbieterAggregations(
        facettenParameter,
        basisFilterParameter
      );
    }),
    shareReplay()
  );
}
